import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { AppLayout } from "./DashboardPage";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { 
  Trophy, 
  Flame,
  Star,
  Target,
  Sparkles,
  Award,
  Crown,
  Zap,
  Shield,
  Medal,
  TrendingUp
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LEVEL_NAMES = [
  'Beginner', 'Apprentice', 'Explorer', 'Adventurer', 'Challenger',
  'Expert', 'Master', 'Champion', 'Legend', 'Grandmaster', 'Elite', 'Mythic'
];

const LEVEL_THRESHOLDS = [0, 100, 250, 500, 1000, 2000, 4000, 7500, 12500, 20000, 35000, 50000];

export default function GamePage() {
  const { household, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [badges, setBadges] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    if (!household) return;
    fetchData();
  }, [household]);

  const fetchData = async () => {
    try {
      const [progressRes, badgesRes, leaderboardRes] = await Promise.all([
        fetch(`${API_URL}/api/households/${household.household_id}/progress`, { credentials: 'include' }),
        fetch(`${API_URL}/api/households/${household.household_id}/badges`, { credentials: 'include' }),
        fetch(`${API_URL}/api/households/${household.household_id}/leaderboard`, { credentials: 'include' })
      ]);

      if (progressRes.ok) setProgress(await progressRes.json());
      if (badgesRes.ok) setBadges(await badgesRes.json());
      if (leaderboardRes.ok) setLeaderboard(await leaderboardRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLevelIcon = (level) => {
    if (level >= 10) return <Crown className="w-6 h-6" />;
    if (level >= 7) return <Medal className="w-6 h-6" />;
    if (level >= 4) return <Award className="w-6 h-6" />;
    return <Star className="w-6 h-6" />;
  };

  const getBadgeIcon = (type) => {
    switch (type) {
      case 'first_reconcile': return Shield;
      case 'streak_7': return Flame;
      case 'budget_master': return Target;
      case 'level_up': return Zap;
      default: return Star;
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  const currentLevel = progress?.level || 1;
  const currentXP = progress?.xp || 0;
  const nextLevelXP = LEVEL_THRESHOLDS[Math.min(currentLevel, LEVEL_THRESHOLDS.length - 1)];
  const prevLevelXP = LEVEL_THRESHOLDS[Math.max(0, currentLevel - 1)];
  const progressPercent = ((currentXP - prevLevelXP) / Math.max(1, nextLevelXP - prevLevelXP)) * 100;

  return (
    <AppLayout>
      <div className="p-6 lg:p-8" data-testid="game-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold">Your Progress</h1>
          <p className="text-muted-foreground">Level up by managing your finances</p>
        </div>

        {/* Main Progress Card */}
        <div className="card-quest p-8 mb-8" data-testid="level-card">
          <div className="flex flex-col md:flex-row md:items-center gap-8">
            {/* Level Display */}
            <div className="flex items-center gap-6">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
                  <span className="font-heading text-4xl font-bold text-white">{currentLevel}</span>
                </div>
                <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-white dark:bg-emerald-950 flex items-center justify-center shadow-md">
                  {getLevelIcon(currentLevel)}
                </div>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Current Level</p>
                <h2 className="font-heading text-2xl font-bold">{LEVEL_NAMES[Math.min(currentLevel - 1, LEVEL_NAMES.length - 1)]}</h2>
              </div>
            </div>

            {/* XP Progress */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Experience Points</span>
                <span className="font-heading text-lg font-bold">{currentXP.toLocaleString()} XP</span>
              </div>
              <Progress value={progressPercent} className="h-4 mb-2" />
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Level {currentLevel}</span>
                <span className="text-muted-foreground">{(nextLevelXP - currentXP).toLocaleString()} XP to Level {currentLevel + 1}</span>
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-6 mt-8 pt-8 border-t border-amber-200 dark:border-amber-800/50">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Flame className="w-5 h-5 text-orange-500" />
                <span className="font-heading text-2xl font-bold">{progress?.streak_days || 0}</span>
              </div>
              <p className="text-sm text-muted-foreground">Day Streak</p>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Trophy className="w-5 h-5 text-amber-500" />
                <span className="font-heading text-2xl font-bold">{badges.length}</span>
              </div>
              <p className="text-sm text-muted-foreground">Badges Earned</p>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
                <span className="font-heading text-2xl font-bold">{currentXP.toLocaleString()}</span>
              </div>
              <p className="text-sm text-muted-foreground">Total XP</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Badges */}
          <div className="card-dashboard">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading font-semibold">Badges</h3>
              <Badge variant="secondary">{badges.length} earned</Badge>
            </div>
            
            {badges.length === 0 ? (
              <div className="text-center py-8">
                <Award className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No badges yet</p>
                <p className="text-sm text-muted-foreground mt-1">Complete tasks to earn your first badge.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {badges.map(badge => {
                  const BadgeIcon = getBadgeIcon(badge.badge_type);
                  return (
                    <div 
                      key={badge.badge_id}
                      className="flex flex-col items-center p-4 rounded-xl bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950/30 dark:to-emerald-950/30 border border-amber-200 dark:border-amber-800/50"
                      data-testid={`badge-${badge.badge_id}`}
                    >
                      <div className="w-12 h-12 rounded-full bg-amber-400 flex items-center justify-center mb-3 shadow-lg shadow-amber-500/30">
                        <BadgeIcon className="w-6 h-6 text-amber-950" />
                      </div>
                      <p className="font-medium text-center text-sm">{badge.name}</p>
                      <p className="text-xs text-muted-foreground text-center mt-1">{badge.description}</p>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Locked Badges Preview */}
            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground mb-4">Upcoming badges</p>
              <div className="flex gap-4">
                {['Savings Pro', 'Debt Crusher', 'Money Master'].map((name, i) => (
                  <div key={i} className="flex flex-col items-center opacity-50">
                    <div className="w-10 h-10 rounded-full bg-stone-200 dark:bg-stone-800 flex items-center justify-center">
                      <Star className="w-5 h-5 text-stone-400" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{name}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Leaderboard */}
          <div className="card-dashboard">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading font-semibold">Household Leaderboard</h3>
            </div>
            
            {leaderboard.length === 0 ? (
              <div className="text-center py-8">
                <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No leaderboard data yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {leaderboard.map((member, index) => (
                  <div 
                    key={member.user_id}
                    className={`flex items-center gap-4 p-4 rounded-xl ${
                      index === 0 
                        ? 'bg-gradient-to-r from-amber-100 to-amber-50 dark:from-amber-950/30 dark:to-emerald-950/20 border border-amber-200 dark:border-amber-800/50' 
                        : 'bg-muted/30'
                    }`}
                    data-testid={`leaderboard-${index}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-amber-400 text-amber-950' :
                      index === 1 ? 'bg-stone-300 text-stone-700' :
                      index === 2 ? 'bg-amber-700 text-amber-100' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {index + 1}
                    </div>
                    
                    <div className="flex-1">
                      <p className="font-medium">{member.name}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>Level {member.level}</span>
                        {member.streak_days > 0 && (
                          <>
                            <span>•</span>
                            <Flame className="w-3 h-3 text-orange-500" />
                            <span>{member.streak_days} streak</span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="font-heading font-bold">{member.xp.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">XP</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* XP Guide */}
        <div className="card-dashboard mt-6">
          <h3 className="font-heading font-semibold mb-4">How to Earn XP</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { action: 'Complete Reconciliation', xp: 50, icon: Target },
              { action: 'Complete Quest', xp: 25, icon: Sparkles },
              { action: 'Set Goal', xp: 20, icon: Flag },
              { action: 'Add Bill', xp: 10, icon: Clock },
              { action: 'Add Transaction', xp: 5, icon: Plus },
              { action: 'Categorize Transaction', xp: 2, icon: Tag },
              { action: 'Daily Streak Bonus', xp: '10/day', icon: Flame },
              { action: 'Level Up Bonus', xp: 'Varies', icon: Zap }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{item.action}</p>
                    <p className="text-xs text-amber-600">+{item.xp} XP</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

// Import icons used in the guide
import { Flag, Clock, Plus, Tag } from "lucide-react";
